import React, { useState, memo, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './FranchiseTaskList.module.scss';
import classNames from 'classnames/bind';
import { BaseTable } from '~/components/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { HeadCelType, SortDirection } from '~/utils/types/common';

import MenuOption from '~/components/franchise-task/menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import usePermission from '~/hooks/usePermission';

import { PERMISSIONS } from '~/utils/constants/permission';
import { IEditTask, IFranchiseTaskList } from '~/utils/interface/task';
import { taskActions } from '~/thunks/task/taskSlice';
import dayjs from 'dayjs';
import { TASK_ACTION_INFO } from '~/utils/constants/task';
import { Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { adminRouteAbsolute } from '~/utils/constants/route';
import StatusLabelTask from '~/components/jobs/status-label-task/StatusLabelTask';

const cx = classNames.bind(styles);

type FranchiseTaskListProps = {
  data?: IEditTask | null;
  onOpenEdit: (id: string) => void;
  onOpenEditJob: (id: string) => void;
  onOpenEditCustomer: (id: string) => void;
};

function FranchiseTaskList(props: FranchiseTaskListProps) {
  const { data, onOpenEditJob, onOpenEditCustomer } = props;
  const permissions = usePermission();
  const { t } = useTranslation(['admin/task', 'admin/job']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t('properties.name'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'type',
        label: t('properties.type'),
        sortable: false,
        searchable: false,
      },
      {
        id: 'status',
        label: t('properties.status'),
        sortable: false,
        searchable: false,
      },
      {
        id: 'dueDate',
        label: t('properties.dueDate'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'description',
        label: t('properties.description'),
        sortable: false,
        searchable: false,
      },
      {
        id: 'createdAt',
        label: t('properties.createdAt'),
        sortable: true,
        searchable: false,
      },
      {
        id: '',
        label: '',
        sortable: false,
        searchable: false,
      },
    ],
    [t]
  );

  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const { taskList, filterFranchiseTaskList, currentPage, totalPages } = useAppSelector((state) => state.task);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    dispatch(taskActions.changeTaskId(itemId));
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(taskActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = async () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      if (data?.jobId) {
        onOpenEditJob(menuSelectedId);
      }
      if (data?.customerId) {
        onOpenEditCustomer(menuSelectedId);
      } else {
        props.onOpenEdit(menuSelectedId);
      }
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(taskActions.changeMenuSelectId(menuSelectedId));
      dispatch(taskActions.changeOpenModalDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      taskActions.setFilterFranchiseTaskList({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      taskActions.setFilterFranchiseTaskList({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  const handleNavigateToTaskDetail = (id: string, type: string) => {
    if (type === 'job') {
      navigate(`${adminRouteAbsolute.jobDetail.replace(':id', id)}#tasks`);
    } else if (type === 'contact') {
      navigate(`${adminRouteAbsolute.contactDetail.replace(':id', id)}#tasks`);
    } else if (type === 'customer') {
      navigate(`${adminRouteAbsolute.customerDetail.replace(':id', id)}#tasks`);
    }
  };
  return (
    <Box className={cx('equipment-list-container')}>
      <BaseTable<IFranchiseTaskList>
        data={taskList}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filterFranchiseTaskList.sort}
        renderCell={(item: IFranchiseTaskList) => {
          return (
            <>
              <TableCell>
                <Link
                  onClick={() => handleNavigateToTaskDetail(item.groupId, item.type)}
                  className='underline text-[#007AFC]'
                >
                  {TASK_ACTION_INFO.includes(item.name as (typeof TASK_ACTION_INFO)[number])
                    ? t(`addTask.${item.name}`, { ns: 'admin/job' })
                    : item.name}
                </Link>
              </TableCell>
              <TableCell> {t(`${item?.typeLabel}`, { ns: 'admin/task' })}</TableCell>
              <TableCell>
                <Box className={cx('status-container')}>
                  <StatusLabelTask variant={item.statusDetail} />
                </Box>
              </TableCell>

              {/* <TableCell>{item.groupId ? t(`${item.statusDetail?.name}`, { ns: 'admin/job' }) : null}</TableCell> */}
              <TableCell>{item.dueDate ? dayjs(item.dueDate).format('MMM DD, YYYY') : null}</TableCell>
              <TableCell>{item.description}</TableCell>
              <TableCell>{dayjs(item.createdAt).format('MMM DD, YYYY')}</TableCell>
              <TableCell>
                {(permissions[PERMISSIONS.companyTask_delete] ||
                  permissions[PERMISSIONS.companyTask_edit] ||
                  permissions[PERMISSIONS.companyTask_view]) && (
                  <Box className={'flex justify-end'}>
                    <Box className={cx('equipment-container')}>
                      <IconButton
                        onClick={(event) => {
                          openOptionMenu(event, item.id);
                        }}
                      >
                        <MenuVerticalIcon className='w-[19px] h-[19px]' />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </TableCell>
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
      />
    </Box>
  );
}

export default memo(FranchiseTaskList);
